import {Integer, ThemeIconKey, Box, Center, Hide, Icon, Show} from 'platform/foundation';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getInitials} from './getInitials';

type AvatarSize = 'small' | 'default' | 'large';

export interface AvatarProps extends TestIdProps {
  size?: 'small' | 'default' | 'large';
  variant?: 'rounded' | 'circular';
  icon?: ThemeIconKey;
  name?: string;
}

export function Avatar(props: AvatarProps) {
  const isNameVisible = isNotNilOrEmpty(props.name) && props.name !== 'null';
  const size = match<AvatarSize, Integer>(props.size ?? 'default')
    .with('small', always(6))
    .with('default', always(10))
    .with('large', always(18))
    .exhaustive();

  const iconSize = match<AvatarSize, Integer>(props.size ?? 'default')
    .with('small', always(3))
    .with('default', always(6))
    .with('large', always(10))
    .exhaustive();

  const textSize = match<AvatarSize, string>(props.size ?? 'default')
    .with('small', always('0.625rem'))
    .with('default', always('1rem'))
    .with('large', always('1.75rem'))
    .exhaustive();

  return (
    <Box
      width={size}
      height={size}
      backgroundColor="accent.lightest"
      borderRadius={props.variant === 'rounded' ? 'small' : 'circular'}
      data-testid={suffixTestId('avatar', props)}
    >
      <Center width={size} height={size}>
        <Show when={isNameVisible}>
          <div
            css={css`
              font-size: ${textSize};
              color: ${({theme}) => theme.colors.text.accent};
              font-family: ${({theme}) => theme.fonts.body};
              font-weight: ${({theme}) => theme.fontWeights.medium};
              white-space: nowrap;
              line-height: 1;
            `}
          >
            {getInitials(props.name ?? '')}
          </div>
        </Show>
        <Hide when={isNameVisible}>
          <Icon value={props.icon ?? 'social/person'} size={iconSize} color="accent.base" />
        </Hide>
      </Center>
    </Box>
  );
}
